import { ref, toValue, type MaybeRefOrGetter } from 'vue'

import type { ApiListResponse } from '@/shared/api'
import { MAX_LIST_LIMIT } from '@/shared/constants'

import { downloadCsv, type ExportCell } from './exportTable'
import { message } from './message'
import type { EntityListParams } from './types'

/** One column of an exported list: its header and how a row fills it. */
export type CsvColumn<TItem> = {
  header: string
  value: (item: TItem) => ExportCell
}

/**
 * Past this many pages the export stops asking and says so, rather than pulling a whole
 * history down one 600-row request at a time.
 */
const MAX_EXPORT_PAGES = 50

/**
 * Export every row a list's filter matches — not the page on screen — as CSV.
 *
 * The API caps a page at `MAX_LIST_LIMIT`, so the rows are fetched page by page until
 * `meta.total` is reached. The columns are the entity's: the file says the same things
 * the table does, in words and plain numbers a spreadsheet can sum.
 */
export function useListCsvExport<
  TItem,
  TParams extends EntityListParams
>(config: {
  getItems: (params: TParams) => Promise<{ data: ApiListResponse<TItem> }>
  /** The list's filter and sort — what the table itself is asked for, minus the page. */
  params: MaybeRefOrGetter<Omit<TParams, 'page' | 'limit'>>
  columns: readonly CsvColumn<TItem>[]
  filename: string
}) {
  const isExporting = ref(false)

  async function fetchAll(): Promise<TItem[] | null> {
    const rows: TItem[] = []

    for (let page = 1; page <= MAX_EXPORT_PAGES; page++) {
      const response = await config.getItems({
        ...toValue(config.params),
        page,
        limit: MAX_LIST_LIMIT
      } as TParams)
      const { data, meta } = response.data

      rows.push(...data)
      if (data.length === 0 || rows.length >= meta.total) return rows
    }

    return null
  }

  async function download() {
    if (isExporting.value) return
    isExporting.value = true

    try {
      const rows = await fetchAll()

      if (rows === null) {
        message.warning(
          'Too many rows to export at once. Narrow the filter and try again.'
        )
      } else if (rows.length === 0) {
        message.warning('Nothing to export for the current filter.')
      } else {
        downloadCsv(
          config.columns.map((column) => column.header),
          rows.map((row) => config.columns.map((column) => column.value(row))),
          config.filename
        )
      }
    } catch {
      message.error('Could not export the list.')
    } finally {
      isExporting.value = false
    }
  }

  return { download, isExporting }
}
